import {
  Select,
  STATUSPAGE,
  FormFeedBack,
  convertEventSelectToTarget,
  formatDataToLabelValue,
  MainButton,
} from '@repo/ui';
import { type GradeCalculationRule } from 'domain/models/params';
import {
  GetAllEntitiesUseCaseInterface,
  GetEvaluationCriteriaByIdUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { useEvaluationCriteria } from 'presentation/app/modules/EvaluationCriteria/hooks/useEvaluationCriteria';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Form,
  Card,
  Label,
  Input,
  Alert,
  Button,
  CardBody,
  CardFooter,
} from 'reactstrap';
import * as Yup from 'yup';

const gradeCalculationRuleOptions = [
  { value: 'WEIGHTED_AVERAGE', label: 'Média ponderada' },
  { value: 'SIMPLE_AVERAGE', label: 'Média simples' },
  { value: 'WEIGHTED_SUM', label: 'Soma ponderada' },
  { value: 'SIMPLE_SUM', label: 'Soma simples' },
];

const gradeCalculationRuleLegends = {
  WEIGHTED_AVERAGE:
    'Calcula a nota final considerando o peso de cada questão. Questões com peso maior têm maior influência no resultado.',
  SIMPLE_AVERAGE:
    'Calcula a média aritmética das notas, sem considerar peso. Todas as questões avaliáveis têm a mesma importância.',
  WEIGHTED_SUM:
    'Soma as notas das questões aplicando o peso definido para cada uma. Questões com peso maior contribuem mais para o total final, sem divisão pela quantidade de questões.',
  SIMPLE_SUM:
    'Soma diretamente as notas das questões, sem considerar peso. Cada questão contribui apenas com sua própria nota.',
} as const;

interface ICriteriaSectionProps {
  id: string;
  criteria: GetEvaluationCriteriaByIdUseCaseInterface.Result;
  organizationsOptions: GetAllEntitiesUseCaseInterface.Result;
}

export const CriteriaSection = ({
  id,
  criteria,
  organizationsOptions,
}: ICriteriaSectionProps) => {
  const navigate = useNavigate();
  const {
    createOrUpdateStatus,
    areInputsAndBtnsDisabled,
    requestUpdateEvaluationCriteria,
    requestCreateEvaluationCriteria,
  } = useEvaluationCriteria();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: criteria ? criteria.name : '',
      organization: criteria?.entity?.id
        ? {
            value: criteria?.entity?.id,
            label: criteria?.entity?.name,
          }
        : null,
      hideWeight: criteria
        ? {
            value: criteria.hiddenWeightage ? 'S' : 'N',
            label: criteria.hiddenWeightage ? 'Sim' : 'Não',
          }
        : { value: 'N', label: 'Não' },
      hideAverage: criteria
        ? {
            value: criteria.hiddenOverall ? 'S' : 'N',
            label: criteria.hiddenOverall ? 'Sim' : 'Não',
          }
        : { value: 'N', label: 'Não' },
      gradeCalculationRule: criteria
        ? gradeCalculationRuleOptions.find(
            (option) => option.value === criteria.gradeCalculationRule,
          ) || gradeCalculationRuleOptions[0]
        : gradeCalculationRuleOptions[0],
      showGrade: criteria
        ? {
            value: criteria.showScore ? 'S' : 'N',
            label: criteria.showScore ? 'Sim' : 'Não',
          }
        : { value: 'N', label: 'Não' },
      instructions: criteria ? criteria.instructions : '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Campo obrigatório'),
      organization: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),
      hideWeight: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),
      hideAverage: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),
      gradeCalculationRule: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),
      showGrade: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),
      instructions: Yup.string().nullable(),
    }),
    onSubmit: (values) => {
      const valuesToSubmit = {
        name: values.name,
        hiddenWeightage: values.hideWeight?.value === 'N' ? false : true,
        hiddenOverall: values.hideAverage?.value === 'N' ? false : true,
        gradeCalculationRule: (values.gradeCalculationRule?.value ||
          'WEIGHTED_AVERAGE') as GradeCalculationRule,
        showScore: values.showGrade?.value === 'N' ? false : true,
        instructions: values.instructions,
        entityId: values?.organization?.value || null,
      };

      if (id) {
        requestUpdateEvaluationCriteria({
          ...valuesToSubmit,
          id,
        });
      } else {
        requestCreateEvaluationCriteria(valuesToSubmit);
      }
    },
  });

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      <Card>
        <CardBody className="p-4">
          <Row className="g-4">
            <Col md={6}>
              <Label htmlFor="name">Nome do Instrumento de Avaliação</Label>
              <Input
                id="name"
                value={validation.values.name}
                onBlur={validation.handleBlur}
                onChange={validation.handleChange}
                name="name"
                invalid={!!(validation.touched.name && validation.errors.name)}
              />
              <FormFeedBack
                error={validation.errors.name as any}
                isInvalid={validation.touched.name && !!validation.errors.name}
              />
            </Col>

            <Col md={6}>
              <Label className="form-label" htmlFor="organization">
                Organização
              </Label>
              <Select
                isClearable
                id="organization"
                name="organization"
                placeholder="Selecione uma organização"
                onBlur={validation.handleBlur}
                value={validation.values.organization}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('organization', e),
                  );
                }}
                options={formatDataToLabelValue(
                  organizationsOptions as never,
                  'id',
                  'name',
                )}
              />
            </Col>

            <Col md={2}>
              <Label className="form-label" htmlFor="hideWeight">
                Ocultar Peso
              </Label>
              <Select
                id="hideWeight"
                name="hideWeight"
                placeholder="Selecione"
                onBlur={validation.handleBlur}
                value={validation.values.hideWeight}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('hideWeight', e),
                  );
                }}
                options={[
                  { value: 'S', label: 'Sim' },
                  { value: 'N', label: 'Não' },
                ]}
              />
            </Col>

            <Col md={2}>
              <Label className="form-label" htmlFor="hideAverage">
                Ocultar Média
              </Label>
              <Select
                id="hideAverage"
                name="hideAverage"
                placeholder="Selecione"
                onBlur={validation.handleBlur}
                value={validation.values.hideAverage}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('hideAverage', e),
                  );
                }}
                options={[
                  { value: 'S', label: 'Sim' },
                  { value: 'N', label: 'Não' },
                ]}
              />
            </Col>

            <Col md={6}>
              <Label className="form-label" htmlFor="gradeCalculationRule">
                Regra de Cálculo da Nota
              </Label>
              <Select
                id="gradeCalculationRule"
                name="gradeCalculationRule"
                placeholder="Selecione"
                onBlur={validation.handleBlur}
                value={validation.values.gradeCalculationRule}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('gradeCalculationRule', e),
                  );
                }}
                options={gradeCalculationRuleOptions}
              />
              {validation.values.gradeCalculationRule?.value && (
                <small className="d-block mt-2 text-muted">
                  {
                    gradeCalculationRuleLegends[
                      validation.values.gradeCalculationRule
                        .value as keyof typeof gradeCalculationRuleLegends
                    ]
                  }
                </small>
              )}
            </Col>

            <Col md={2}>
              <Label className="form-label" htmlFor="showGrade">
                Exibir Nota
              </Label>
              <Select
                id="showGrade"
                name="showGrade"
                placeholder="Selecione"
                onBlur={validation.handleBlur}
                value={validation.values.showGrade}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('showGrade', e),
                  );
                }}
                options={[
                  { value: 'S', label: 'Sim' },
                  { value: 'N', label: 'Não' },
                ]}
              />
            </Col>

            <Col md={12}>
              <Label htmlFor="instructions" className="form-label">
                Instrução
              </Label>
              <Input
                rows={4}
                id="instructions"
                type="textarea"
                name="instructions"
                className="form-control"
                onBlur={validation.handleBlur}
                value={validation.values?.instructions}
                onChange={validation.handleChange}
              />
            </Col>

            <Col md={12}>
              <Alert
                className="alert-icon"
                style={{
                  backgroundColor: '#FEF8E2',
                  borderColor: '#EBE7CA',
                  color: '#c9ab40',
                }}
              >
                <i className="ri-information-line me-2 fs-16"></i>
                <strong>Atenção!</strong> Quaisquer alterações realizadas não
                terão impacto nas avaliações anteriores dos estudantes
              </Alert>
            </Col>
          </Row>
        </CardBody>

        <CardFooter className="d-flex justify-content-end gap-2">
          <Button
            className="btn-link text-decoration-underline"
            color="link"
            onClick={() => navigate('/cadastros/instrumentos-de-avaliacao')}
          >
            Cancelar
          </Button>
          <MainButton
            type="submit"
            isDisabled={areInputsAndBtnsDisabled}
            isLoading={createOrUpdateStatus === STATUSPAGE.LOADING}
          >
            Salvar
          </MainButton>
        </CardFooter>
      </Card>
    </Form>
  );
};
